import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "@phosphor-icons/react";
import { GoogleSignInButton, AuthDivider } from "@/components/GoogleSignInButton";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="overline text-primary">← Back home</Link>
          <h1 className="font-serif text-4xl font-medium mt-6 mb-2">Create account</h1>
          <p className="text-muted-foreground mb-8">Start building your business today.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" value={name} required
                onChange={(e) => setName(e.target.value)}
                className="mt-2 bg-zinc-900/60 border-white/10"
                data-testid="register-name-input" placeholder="Jane Doe" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} required
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-zinc-900/60 border-white/10"
                data-testid="register-email-input" placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} required minLength={6}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-zinc-900/60 border-white/10"
                data-testid="register-password-input" placeholder="Min 6 characters" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="register-submit-button"
              className="w-full bg-primary text-primary-foreground hover:bg-[#DBC197] active:scale-95 transition-transform">
              {loading ? "Creating..." : <>Create account <ArrowRight size={18} className="ml-2" /></>}
            </Button>
          </form>

          <AuthDivider />
          <GoogleSignInButton label="Sign up with Google" />

          <p className="mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-primary hover:underline" data-testid="go-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="relative hidden lg:block order-1 lg:order-2">
        <img
          src="https://images.pexels.com/photos/34823910/pexels-photo-34823910.jpeg"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-zinc-950/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="font-display text-2xl tracking-tighter mb-2">DAFARGOS HOLDINGS LLC</div>
          <p className="text-zinc-300 max-w-sm font-serif text-2xl leading-snug">
            From idea to established business — guided every step.
          </p>
        </div>
      </div>
    </div>
  );
}
